import { useNavigate, useParams } from '@tanstack/react-router';
import { Button, toast } from '@devhop/ui';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import { Header } from '@/app/layout/Header';
import { Main } from '@/app/layout/Main';
import type { UpdatePromotionRecordDTO } from '@/modules/hr/promotion/domain/types';
import {
    usePromotionLevelsLookupQuery,
    usePromotionRecordDetailQuery,
    useUpdatePromotionRecordMutation,
} from '../api/queries';
import { PromotionRecordForm } from '../ui/PromotionRecordForm';
import {
    PromotionTour,
    promotionRecordEditTourSteps,
    usePromotionTour,
} from '../tour';

const backSearch = {
    offset: 0,
    limit: 20,
    sort: undefined,
    employeeId: undefined,
    periodYear: undefined,
    periodMonth: undefined,
    positionId: undefined,
    search: undefined,
};

export function PromotionRecordEditPage() {
    const nav = useNavigate();
    const { id } = useParams({ from: '/app/hr/promotion/records/$id/edit' });
    const detailQuery = usePromotionRecordDetailQuery(id);
    const updateMutation = useUpdatePromotionRecordMutation();
    const levelsQuery = usePromotionLevelsLookupQuery();

    const back = () =>
        nav({
            to: '/app/hr/promotion/records',
            search: backSearch,
        });

    const goToDetail = () =>
        id &&
        nav({
            to: '/app/hr/promotion/records/$id',
            params: { id },
            search: backSearch,
        });

    const submit = (values: UpdatePromotionRecordDTO) => {
        if (!id) return;
        updateMutation.mutate(
            { id, dto: values },
            {
                onSuccess: () => {
                    toast.success('ບັນທຶກການແກ້ໄຂແລ້ວ');
                    goToDetail();
                },
                onError: (err) =>
                    toast.error(err?.message ?? 'ບັນທຶກການແກ້ໄຂບໍ່ສຳເລັດ'),
            },
        );
    };

    const item = detailQuery.data;
    const tour = usePromotionTour('recordEdit');

    return (
        <>
            <Header />
            <Main>
                <PromotionTour
                    steps={promotionRecordEditTourSteps}
                    run={tour.run}
                    onCallback={tour.handleJoyrideCallback}
                />
                <div
                    className="mb-6 flex items-center justify-between gap-2"
                    data-tourid="promotion-record-edit-back"
                >
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-semibold text-2xl">
                                ແກ້ໄຂບັນທຶກການເລື່ອນຕຳແໜ່ງ
                            </h1>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="size-7 rounded-full"
                                onClick={tour.startTour}
                                title="ເລີ່ມທົດສອບການນຳທາງ"
                                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                            >
                                <HelpCircle className="h-4 w-4" />
                            </Button>
                        </div>
                        {item && (
                            <p className="text-muted-foreground text-sm">
                                {item.employeeName}
                            </p>
                        )}
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={back}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        ກັບຄືນ
                    </Button>
                </div>

                {detailQuery.isLoading && (
                    <div className="mx-auto max-w-3xl">
                        <p className="text-muted-foreground text-sm">
                            ກຳລັງໂຫຼດ...
                        </p>
                    </div>
                )}

                {!detailQuery.isLoading && !item && (
                    <div className="mx-auto max-w-3xl">
                        <p className="text-muted-foreground text-sm">
                            ບໍ່ພົບບັນທຶກ
                        </p>
                    </div>
                )}

                {!detailQuery.isLoading && item && (
                    <div className="mx-auto max-w-3xl space-y-6" data-tourid="promotion-record-edit-form">
                        <PromotionRecordForm
                            mode="edit"
                            initialValues={item}
                            onUpdate={submit}
                            onCancel={goToDetail}
                            isSubmitting={updateMutation.isPending}
                            levelOptions={(levelsQuery.data ?? []).map((l) => ({
                                value: l.id,
                                label: l.label,
                            }))}
                        />
                    </div>
                )}
            </Main>
        </>
    );
}
